import React from 'react';
import PageMeta from '../components/PageMeta';
import { Mail, MessageSquare, CreditCard, FileText, MapPin, Phone, UserRound } from 'lucide-react';

const officialDetails = [
  {
    icon: <UserRound className="w-5 h-5" />,
    label: 'Preparator Name',
    value: 'Pradeepa Subramni',
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    label: 'Address',
    value: [
      'MVM Aprajithapuri',
      'B Block, BF3, Manikandapuram,',
      'Thirumullaivoyal,',
      'Chennai - 600062',
    ],
  },
  {
    icon: <Phone className="w-5 h-5" />,
    label: 'Phone Number',
    value: '+91-6379763729',
    href: 'tel:+916379763729',
  },
  {
    icon: <Mail className="w-5 h-5" />,
    label: 'Email',
    value: 'contact@thebtree.com',
    href: 'mailto:contact@thebtree.com',
  },
];

const contactCards = [
  {
    icon: <Mail className="w-5 h-5" />,
    title: 'General Support',
    text: 'For account access, subscription questions or platform assistance.',
    href: 'mailto:contact@thebtree.com',
    label: 'contact@thebtree.com',
  },
  {
    icon: <CreditCard className="w-5 h-5" />,
    title: 'Billing and Payments',
    text: 'For duplicate payments, activation issues, invoices or subscription billing.',
    href: 'mailto:contact@thebtree.com?subject=Billing%20Support',
    label: 'Billing support',
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: 'Research Access',
    text: 'For report access issues, content availability or subscription entitlements.',
    href: 'mailto:contact@thebtree.com?subject=Research%20Access',
    label: 'Research support',
  },
];

const Contact = () => {
  return (
    <>
      <PageMeta
        title="Contact The B Tree | Support, Billing & Research Access"
        description="Contact The B Tree for account help, subscription support, billing questions, and research access requests."
        canonical="https://www.thebtree.com/contact"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <main className="min-h-screen bg-[#F7F5F2]" data-testid="contact-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-18">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            Contact BTree
          </p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-5">
            Get help with your account, subscription or research access.
          </h1>
          <p className="text-lg text-slate-700 leading-8 max-w-3xl">
            Send us the details of your request and the BTree support team will route it to the right workflow.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm mb-10">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            Contact Details
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {officialDetails.map((item) => (
              <div key={item.label} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#C8A84B]/10 text-[#9B7A21] flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
                    {item.label}
                  </h2>
                  {Array.isArray(item.value) ? (
                    <address className="not-italic text-slate-800 leading-7">
                      {item.value.map((line) => (
                        <span key={line} className="block">{line}</span>
                      ))}
                    </address>
                  ) : item.href ? (
                    <a
                      href={item.href}
                      className="text-slate-800 leading-7 hover:text-[#9B7A21] underline underline-offset-4"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-slate-800 leading-7">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </article>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {contactCards.map((card) => (
            <article key={card.title} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#C8A84B]/10 text-[#9B7A21] flex items-center justify-center mb-4">
                {card.icon}
              </div>
              <h2 className="text-2xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-2">
                {card.title}
              </h2>
              <p className="text-slate-700 leading-7 mb-5">
                {card.text}
              </p>
              <a
                href={card.href}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#9B7A21] hover:text-[#0D1B2A] underline underline-offset-4"
              >
                {card.label}
              </a>
            </article>
          ))}
        </div>

        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[#0D1B2A] text-white items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
                Faster Resolution
              </p>
              <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-4">
                Include the details we need.
              </h2>
              <p className="text-slate-700 leading-8 mb-5">
                For subscription or payment support, include your name, registered email address, transaction
                reference and a short description of the issue.
              </p>
              <a
                href="mailto:contact@thebtree.com"
                className="inline-flex items-center justify-center rounded-full bg-[#0D1B2A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1A2B3F]"
              >
                Email Support
              </a>
            </div>
          </div>
        </article>
      </section>
    </main>
    </>
  );
};

export default Contact;
